import type { ElementRef } from '../../../../lib/teact/teact';
import { getIsHeavyAnimating, useEffect, useRef } from '../../../../lib/teact/teact';

import { requestMeasure } from '../../../../lib/fasterdom/fasterdom';

import useHeavyAnimation from '../../../../hooks/useHeavyAnimation';
import useLastCallback from '../../../../hooks/useLastCallback';
import usePriorityPlaybackCheck, { isPriorityPlaybackActive } from '../../../../hooks/usePriorityPlaybackCheck';
import useBackgroundMode, { isBackgroundModeActive } from '../../../../hooks/window/useBackgroundMode';

export default function useVideoAutoPause(
  playerRef: ElementRef<HTMLVideoElement>, canPlay: boolean, isPriority?: boolean,
) {
  const canPlayRef = useRef();
  canPlayRef.current = canPlay;

  const { play, pause } = usePlayPause(playerRef);

  const unfreezePlaying = useLastCallback(() => {
    if (canPlayRef.current && (isPriority || !isFrozen())) {
      play();
    }
  });

  const unfreezePlayingOnRaf = useLastCallback(() => {
    requestMeasure(unfreezePlaying);
  });

  useBackgroundMode(pause, unfreezePlayingOnRaf, !canPlay || isPriority);
  useHeavyAnimation(pause, unfreezePlaying, !canPlay || isPriority);
  usePriorityPlaybackCheck(pause, unfreezePlaying, !canPlay || isPriority);

  const handlePlaying = useLastCallback(() => {
    if (!canPlayRef.current || (!isPriority && isFrozen())) {
      pause();
    }
  });

  useEffect(() => {
    if (canPlay) {
      if (isPriority || !isFrozen()) {
        play();
      }
    } else {
      pause();
    }
  }, [canPlay, play, pause, isPriority]);

  return { handlePlaying };
}

function usePlayPause(mediaRef: ElementRef<HTMLMediaElement>) {
  const shouldPauseRef = useRef(false);
  const isLoadingPlayRef = useRef(false);
  const playRequestIdRef = useRef(0);

  const play = useLastCallback(() => {
    const mediaEl = mediaRef.current;

    shouldPauseRef.current = false;
    if (mediaEl && !isLoadingPlayRef.current && document.body.contains(mediaEl)) {
      const requestId = ++playRequestIdRef.current;
      isLoadingPlayRef.current = true;
      mediaEl.play().then(() => {
        if (requestId !== playRequestIdRef.current) {
          return;
        }

        isLoadingPlayRef.current = false;
        if (shouldPauseRef.current) {
          mediaEl.pause();
          shouldPauseRef.current = false;
        }
      }).catch((e) => {
        if (requestId !== playRequestIdRef.current) {
          return;
        }

        isLoadingPlayRef.current = false;
        shouldPauseRef.current = false;

        if (isExpectedPlayInterruptionError(e, mediaEl)) {
          return;
        }

        // eslint-disable-next-line no-console
        console.warn(e);
      });
    }
  });

  const pause = useLastCallback(() => {
    if (isLoadingPlayRef.current) {
      shouldPauseRef.current = true;
    } else {
      mediaRef.current?.pause();
    }
  });

  useEffect(() => {
    return () => {
      playRequestIdRef.current += 1;
      isLoadingPlayRef.current = false;
      shouldPauseRef.current = false;
    };
  }, []);

  return { play, pause };
}

function isFrozen() {
  return getIsHeavyAnimating() || isPriorityPlaybackActive() || isBackgroundModeActive();
}

function isExpectedPlayInterruptionError(error: unknown, mediaEl: HTMLMediaElement) {
  if (!document.body.contains(mediaEl)) {
    return true;
  }

  return isAbortError(error);
}

function isAbortError(error: unknown) {
  if (error instanceof DOMException) {
    return error.name === 'AbortError';
  }

  if (!error || typeof error !== 'object') {
    return false;
  }

  return 'name' in error && error.name === 'AbortError';
}
